
import { Header } from '../../../components/feature/Header';
import { Footer } from '../../../components/feature/Footer';

export default function NewConstructionBoom2025Page() {
  return (
    <div className="min-h-screen">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="mb-8">
            <div className="text-sm text-gray-500 mb-4">September 25, 2025</div>
            <h1 className="text-4xl font-bold text-black mb-6">
              New Construction Update: Q3 2025 Results & 2026 Pipeline
            </h1>
            <img 
              src="https://public.readdy.ai/ai/img_res/48f151fa9ac5dff3d9572234ecca923c.jpg"
              alt="New Construction Q3 2025 Update"
              className="w-full h-64 object-cover object-top rounded-lg mb-8"
            />
          </div>

          <article className="prose prose-lg max-w-none">
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
              <h3 className="text-xl font-bold text-black mb-2">Q3 2025 Construction Results</h3>
              <ul className="text-gray-700 space-y-2">
                <li>• 4,850 new homes delivered across tri-county area</li>
                <li>• $1.9 billion in construction value completed</li>
                <li>• 2026 pipeline: 22,000+ homes planned or under construction</li>
                <li>• Average new home price: $425,000 (up 8% from Q3 2024)</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-black mb-4">Q3 2025 Delivery Summary</h2>
            <p className="text-gray-700 mb-6">
              The third quarter of 2025 marked a significant milestone in Metro Detroit's construction boom, with builders delivering nearly 5,000 new homes—the highest quarterly total since 2007. This surge in completions, combined with an unprecedented pipeline for 2026, positions the region for continued growth and housing accessibility improvements.
            </p>

            <h2 className="text-2xl font-bold text-black mb-4">Completed Projects - Q3 2025</h2>
            
            <div className="space-y-8 mb-8">
              <div className="bg-white border-2 border-yellow-500 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-3">Oakland County Completions</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-black mb-2">Auburn Hills Commons - Phase 1</h4>
                    <p className="text-gray-700 mb-2">285 homes delivered, $350K-$650K range</p>
                    <ul className="text-gray-600 text-sm space-y-1">
                      <li>• 98% sold before completion</li>
                      <li>• Smart home technology standard in all units</li>
                      <li>• Community fitness center and trails now open</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-black mb-2">Troy Meadows - Phase 2</h4>
                    <p className="text-gray-700 mb-2">156 luxury homes delivered, $500K-$850K range</p>
                    <ul className="text-gray-600 text-sm space-y-1">
                      <li>• Golf course community with premium amenities</li>
                      <li>• Energy Star certified construction</li>
                      <li>• Waiting list established for Phase 3</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white border-2 border-yellow-500 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-3">Macomb County Completions</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-black mb-2">Shelby Township Crossing - Phase 1</h4>
                    <p className="text-gray-700 mb-2">425 homes delivered, $280K-$480K range</p>
                    <ul className="text-gray-600 text-sm space-y-1">
                      <li>• Mixed-use development with retail and dining</li>
                      <li>• First-time buyer friendly pricing</li>
                      <li>• 92% occupancy rate achieved</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-black mb-2">Clinton River Estates - Waterfront Phase</h4>
                    <p className="text-gray-700 mb-2">89 waterfront homes delivered, $450K-$750K range</p>
                    <ul className="text-gray-600 text-sm space-y-1">
                      <li>• Private marina and boat slips included</li>
                      <li>• Luxury finishes and custom options</li>
                      <li>• All units sold with premium pricing achieved</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white border-2 border-yellow-500 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-3">Wayne County Completions</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-black mb-2">Livonia Gardens - Phase 2</h4>
                    <p className="text-gray-700 mb-2">380 family homes delivered, $320K-$550K range</p>
                    <ul className="text-gray-600 text-sm space-y-1">
                      <li>• Top-rated Livonia school district</li>
                      <li>• Community parks and recreation facilities</li>
                      <li>• Strong resale values already emerging</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-black mb-2">Dearborn Heights Village - Starter Homes</h4>
                    <p className="text-gray-700 mb-2">195 entry-level homes delivered, $280K-$420K range</p>
                    <ul className="text-gray-600 text-sm space-y-1">
                      <li>• Designed for first-time buyers</li>
                      <li>• Modern open floor plans</li>
                      <li>• 85% purchased by owner-occupants</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-black mb-4">2026 Construction Pipeline</h2>
            
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h3 className="text-xl font-bold text-black mb-4">Major Projects Launching in 2026</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-black mb-3">Q1 2026 Launches</h4>
                  <ul className="text-gray-700 space-y-2">
                    <li>• Novi Tech Corridor: 1,200 homes ($400K-$700K)</li>
                    <li>• Sterling Heights Commons: 850 homes ($320K-$520K)</li>
                    <li>• Canton Family Estates: 675 homes ($350K-$580K)</li>
                    <li>• Rochester Hills Luxury: 320 homes ($600K-$1.2M)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-black mb-3">Q2-Q3 2026 Launches</h4>
                  <ul className="text-gray-700 space-y-2">
                    <li>• Farmington Hills Modern: 950 homes ($380K-$650K)</li>
                    <li>• Warren Industrial District: 1,100 homes ($290K-$450K)</li>
                    <li>• Plymouth Historic District: 425 homes ($450K-$750K)</li>
                    <li>• Bloomfield Township Estates: 280 homes ($700K-$1.5M)</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-black mb-4">2026 Construction Trends</h2>
            
            <div className="space-y-6 mb-8">
              <div className="bg-white border-2 border-yellow-500 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-3">Sustainability Focus</h3>
                <p className="text-gray-700 mb-3">
                  2026 projects emphasizing environmental responsibility and energy efficiency as standard features.
                </p>
                <ul className="text-gray-700 space-y-1">
                  <li>• Solar panels standard on 75% of new construction</li>
                  <li>• EV charging stations in all garages</li>
                  <li>• Smart home energy management systems</li>
                  <li>• Sustainable building materials and water conservation</li>
                  <li>• LEED certification targeting for major developments</li>
                </ul>
              </div>

              <div className="bg-white border-2 border-yellow-500 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-3">Technology Integration</h3>
                <p className="text-gray-700 mb-3">
                  Advanced technology becoming standard rather than upgrade option in 2026 builds.
                </p>
                <ul className="text-gray-700 space-y-1">
                  <li>• Whole-home automation systems included</li>
                  <li>• High-speed fiber internet infrastructure</li>
                  <li>• Smart security and monitoring systems</li>
                  <li>• Voice-controlled lighting and climate</li>
                  <li>• Integrated home office spaces with tech infrastructure</li>
                </ul>
              </div>

              <div className="bg-white border-2 border-yellow-500 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-3">Community Amenities</h3>
                <p className="text-gray-700 mb-3">
                  2026 developments focusing on lifestyle amenities and community building features.
                </p>
                <ul className="text-gray-700 space-y-1">
                  <li>• Resort-style pools and fitness centers</li>
                  <li>• Co-working spaces and business centers</li>
                  <li>• Dog parks and pet-friendly amenities</li>
                  <li>• Walking trails and outdoor recreation</li>
                  <li>• Community gardens and outdoor gathering spaces</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-black mb-4">Pricing Trends and Affordability</h2>
            <p className="text-gray-700 mb-4">
              Despite rising construction costs, builders are implementing strategies to maintain affordability:
            </p>
            <ul className="text-gray-700 mb-6 space-y-2">
              <li>• Entry-level homes starting at $280K in select communities</li>
              <li>• Builder incentives averaging $15K-$25K in Q4 2025</li>
              <li>• Rate buydown programs reducing monthly payments</li>
              <li>• Flexible financing options with preferred lenders</li>
              <li>• Trade-in programs for existing homeowners</li>
            </ul>

            <h2 className="text-2xl font-bold text-black mb-4">Investment Opportunities in New Construction</h2>
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h3 className="text-xl font-bold text-black mb-4">Why Investors Are Choosing New Construction</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-black mb-3">Financial Advantages</h4>
                  <ul className="text-gray-700 space-y-2">
                    <li>• Lower maintenance costs for 5-10 years</li>
                    <li>• Builder warranties protecting investment</li>
                    <li>• Energy efficiency reducing operating costs</li>
                    <li>• Strong rental demand for modern amenities</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-black mb-3">Market Position</h4>
                  <ul className="text-gray-700 space-y-2">
                    <li>• Premium rental rates for new construction</li>
                    <li>• Faster lease-up times in desirable communities</li>
                    <li>• Appreciation potential in growing submarkets</li>
                    <li>• Tax advantages for energy-efficient features</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-black mb-4">Getting Positioned for 2026</h2>
            <p className="text-gray-700 mb-6">
              With the largest construction pipeline in over 15 years, 2026 presents unprecedented opportunities for buyers at every price point. Early positioning in preferred communities will be crucial as demand is expected to exceed supply in the most desirable locations.
            </p>

            <div className="bg-black text-white rounded-lg p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Secure Your Spot in 2026's Best Communities</h3>
              <p className="text-gray-300 mb-6">
                With limited lots available in the most sought-after developments, early action is essential. Let me help you navigate the new construction process and secure priority positioning.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="bg-yellow-500 text-black px-8 py-3 rounded-lg hover:bg-yellow-400 transition-colors font-semibold cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-building-line mr-2"></i>
                  Explore 2026 Communities
                </button>
                <button className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg hover:bg-white hover:text-black transition-colors font-semibold cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line mr-2"></i>
                  Call: (248) 248-4801
                </button>
              </div>
            </div>
          </article>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
